import { Component } from '@theme/component';

/**
 * A custom element that handles contact form submissions through the
 * `/apps/contacts/drawer` Shopify App Proxy.
 *
 * The contacts app expects a single `payload` field with the JSON-stringified
 * data plus any file attachments under the `attachments` key, so this component
 * intercepts the native form submission, builds the payload according to the
 * `data-section` attribute (orders / stores / business) and posts a multipart
 * `FormData` request.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form - The contact form element.
 * @property {HTMLElement} formWrapper - The wrapper around the form.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} submitButtonText - The submit button text element.
 *
 * @extends {Component<Refs>}
 */
class ContactFormComponent extends Component {
  requiredRefs = ['form', 'formWrapper', 'submitButton', 'submitButtonText'];

  /**
   * Updates the file-name display next to a file input when the user selects files.
   *
   * @param {Event} event - The change event on the file input.
   */
  updateFileName = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'file') return;

    const wrapper = input.closest('.contact-block-drawer__file-upload-wrapper');
    if (!wrapper) return;

    const display = wrapper.querySelector('.contact-block-drawer__file-name');
    if (!(display instanceof HTMLElement)) return;

    const files = Array.from(input.files ?? []);

    if (files.length === 0) {
      display.textContent = '';
      return;
    }

    if (files.length === 1) {
      display.textContent = files[0]?.name ?? '';
      return;
    }

    const filesSelectedLabel = this.dataset.filesSelectedLabel ?? 'files selected';
    display.textContent = `${files.length} ${filesSelectedLabel}`;
  };

  /**
   * Forwards a click on the visible upload button to the hidden file input.
   *
   * @param {MouseEvent} event - The click event on the upload button.
   */
  triggerFileUpload = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const wrapper = button.closest('.contact-block-drawer__file-upload-wrapper');
    const input = wrapper?.querySelector('.contact-block-drawer__media-input');
    if (input instanceof HTMLInputElement) input.click();
  };

  /**
   * Intercepts the form submission, builds the contacts-app payload from the
   * form fields and sends it as multipart `FormData`.
   *
   * @param {SubmitEvent} event - The form submit event.
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const sectionType = this.dataset.section;
    if (!sectionType) return;

    const { form, submitButton, submitButtonText } = this.refs;

    const originalText = submitButtonText.textContent ?? '';
    submitButton.disabled = true;
    submitButtonText.textContent = this.dataset.sendingLabel ?? originalText;

    try {
      const formData = new FormData();
      const formObject = Object.fromEntries(new FormData(form).entries());
      const storeName = window.THEME_INFO?.brand_name ?? '';
      let mappedData = {};

      switch (sectionType) {
        case 'orders': {
          const requestTypeSelect = form.querySelector('select[name="orderRequestType"]');
          mappedData = {
            type: 'orders',
            store: storeName,
            firstName: formObject.orderName,
            lastName: formObject.orderSurname,
            email: formObject.orderEmail,
            orderNumber: formObject.orderOrderNumber,
            requestTypeLabel: this.#selectedLabel(requestTypeSelect),
            message: formObject.orderRequestMessage,
            subject: this.dataset.ordersTitle ?? '',
          };

          this.#appendFiles(formData, form.querySelectorAll('input[name="orderMediaUpload[]"]'));
          break;
        }

        case 'stores': {
          const requestTypeSelect = form.querySelector('select[name="storesRequestType"]');
          const pointOfSaleSelect = form.querySelector('select[name="storesRequestPointOfSale"]');
          const pointOfSaleValue = pointOfSaleSelect instanceof HTMLSelectElement ? pointOfSaleSelect.value : '';

          mappedData = {
            type: 'stores',
            store: storeName,
            firstName: formObject.storesName,
            lastName: formObject.storesSurname,
            email: formObject.storesEmail,
            pointOfSale: formObject.storesRequestPointOfSale,
            purchaseDate: formObject.storesPurchaseDate,
            storesHiddenInput: this.#selectedLabel(requestTypeSelect),
            storesRequestPointOfSaleInput: this.#selectedLabel(pointOfSaleSelect),
            storesRequestPointOfSaleInputValue: pointOfSaleValue,
            message: formObject.storesRequestMessage,
            subject: this.dataset.storesTitle ?? '',
          };

          this.#appendFiles(formData, form.querySelectorAll('input[name="storesMediaUpload[]"]'));
          break;
        }

        case 'business': {
          const requestTypeSelect = form.querySelector('select[name="businessRequestType"]');
          const businessRequestTypeValue =
            requestTypeSelect instanceof HTMLSelectElement ? requestTypeSelect.value : '';

          mappedData = {
            type: 'business',
            store: storeName,
            firstName: formObject.businessName,
            lastName: formObject.businessSurname,
            email: formObject.businessEmail,
            company: formObject.businessCompany,
            requestTypeLabel: this.#selectedLabel(requestTypeSelect),
            businessRequestTypeValue,
            subject: this.dataset.businessTitle ?? '',
            message: formObject.businessRequestMessage,
          };

          this.#appendFiles(formData, form.querySelectorAll('input[name="businessMediaUpload[]"]'));
          break;
        }

        default:
          submitButton.disabled = false;
          submitButtonText.textContent = originalText;
          return;
      }

      formData.append('payload', JSON.stringify(mappedData));

      const response = await fetch('/apps/contacts/drawer', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      this.#showSuccess();
    } catch (error) {
      console.error('Error submitting contact form:', error);
      submitButton.disabled = false;
      submitButtonText.textContent = originalText;
    }
  };

  /**
   * Returns the trimmed `textContent` of the currently selected option of a
   * `<select>` element, or an empty string when the placeholder option is
   * still selected (matching the legacy behaviour where unset selects produce
   * an empty label in the payload).
   *
   * @param {Element | null} select - The select element to read from.
   * @returns {string} The selected option label.
   */
  #selectedLabel(select) {
    if (!(select instanceof HTMLSelectElement)) return '';

    const selected = select.options[select.selectedIndex];
    if (!selected || selected.disabled) return '';

    return (selected.textContent ?? '').trim();
  }

  /**
   * Appends every selected file from the given file inputs to the FormData
   * under the `attachments` key.
   *
   * @param {FormData} formData - The FormData to append files to.
   * @param {NodeListOf<Element>} inputs - The file input elements.
   */
  #appendFiles(formData, inputs) {
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      for (const file of input.files ?? []) {
        formData.append('attachments', file, file.name);
      }
    }
  }

  #showSuccess() {
    const { formWrapper } = this.refs;
    const successLabel = this.dataset.successLabel ?? 'Request sent successfully';

    formWrapper.remove();

    const successEl = document.createElement('div');
    successEl.className = 'contact-block-drawer__success';
    successEl.innerHTML = `
      <svg width="96" height="96" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <h2 class="body-l-medium">${successLabel}</h2>
    `;
    this.appendChild(successEl);
  }
}

if (!customElements.get('contact-form')) {
  customElements.define('contact-form', ContactFormComponent);
}
